import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Cross, Heart, Flame, Bird, Mountain, ArrowRight } from "lucide-react";

const FRANCIS = "https://static.prod-images.emergentagent.com/jobs/fd0f3bbe-b357-48f6-aef4-a23c6e5b4fd7/images/5519b50be37625ff4d4ca018949e6f7888ded3c7678c3aedde031970f954a2e3.png";
const ASSISI = "https://images.unsplash.com/photo-1516108317508-6788f6a160e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const TIMELINE = [
  { year: "1181/82", icon: Heart, title: "Nacimiento en Asís", text: "Nace Giovanni di Pietro di Bernardone en la pequeña ciudad de Asís, en la Umbría italiana." },
  { year: "1202", icon: Mountain, title: "Guerra y prisión", text: "Participa en la guerra entre Asís y Perugia. Es hecho prisionero por casi un año." },
  { year: "1205", icon: Cross, title: "Cristo de San Damián", text: "Escucha la voz de Cristo crucificado: «Francisco, repara mi Iglesia». Inicia su conversión." },
  { year: "1206", icon: Heart, title: "Renuncia a los bienes", text: "Frente al obispo de Asís devuelve sus vestidos a su padre. Se entrega totalmente a Dios." },
  { year: "1209", icon: Flame, title: "Nace la Orden", text: "Con sus primeros compañeros redacta una breve regla. El Papa Inocencio III la aprueba oralmente." },
  { year: "1212", icon: Bird, title: "Santa Clara se une", text: "Clara de Favarone abraza la vida franciscana y funda la rama femenina (Clarisas)." },
  { year: "1219", icon: Cross, title: "Encuentro con el Sultán", text: "Viaja a Egipto y se encuentra pacíficamente con el sultán Al-Kamil. Diálogo profético." },
  { year: "1224", icon: Cross, title: "Estigmas en La Verna", text: "Recibe los estigmas de la Pasión de Cristo en el monte La Verna." },
  { year: "1225", icon: Mountain, title: "Cántico de las Criaturas", text: "Compone el primer poema en lengua italiana, alabando a Dios por toda la creación." },
  { year: "1226", icon: Flame, title: "Pascua de San Francisco", text: "El 3 de octubre, en la Porciúncula, pasa al Padre cantando. Inicia su Pascua eterna." },
  { year: "1228", icon: Heart, title: "Canonización", text: "El Papa Gregorio IX lo canoniza apenas dos años después de su muerte." },
  { year: "2026", icon: Flame, title: "Octavo Centenario", text: "La Iglesia y la familia franciscana celebran 800 años de la Pascua del Poverello." },
];

const QUOTES = [
  "Señor, hazme instrumento de tu paz.",
  "Donde hay odio, ponga yo amor.",
  "Comenzad por lo necesario, después lo posible, y al fin estaréis haciendo lo imposible.",
  "Predica el Evangelio siempre. Si es necesario, usa palabras.",
  "Alabado seas, mi Señor, por la hermana nuestra madre tierra.",
  "Nadie debería conformarse con lo que tiene si lo que tiene no le da paz.",
];

export default function Centenario() {
  const [extra, setExtra] = useState([]);
  const [cms, setCms] = useState({});

  useEffect(() => {
    api.get("/content?section=centenario").then((r) => setExtra(r.data)).catch(() => {});
    api.get("/site-settings").then((r) => setCms(r.data?.centenario || {})).catch(() => {});
  }, []);

  const heroBanner = cms.banner_url || ASSISI;
  const portrait = cms.image_url || FRANCIS;

  return (
    <div data-testid="centenario-page">
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px] flex items-end overflow-hidden">
        <img src={heroBanner} alt="Asís" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/70 to-transparent" />
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-12 pb-16 text-primary-foreground">
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-3">1226 — 2026</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95]" data-testid="centenario-title">
            {cms.title || (<>Octavo Centenario<br /><em>de la Pascua</em></>)}
          </h1>
          <p className="mt-5 font-display text-2xl md:text-3xl italic max-w-3xl" data-testid="centenario-subtitle">
            {cms.subtitle || "Ocho siglos celebrando al hermano universal: San Francisco de Asís."}
          </p>
          {cms.paragraph && (
            <p className="mt-4 text-base leading-relaxed max-w-3xl opacity-90" data-testid="centenario-paragraph">{cms.paragraph}</p>
          )}
        </div>
      </section>

      {/* Bio intro */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-20 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-1">
          <div className="aspect-[3/4] rounded-3xl overflow-hidden bg-gradient-to-br from-secondary/20 to-primary/10 sticky top-24">
            <img src={portrait} alt="San Francisco" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-2">
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-4">Quien fue</p>
          <h2 className="font-display text-4xl md:text-5xl text-primary leading-tight mb-6">
            El Poverello que abrazó al mundo con el corazón del Evangelio.
          </h2>
          <div className="space-y-5 text-lg leading-relaxed text-foreground/80">
            <p>
              San Francisco de Asís (1181/82–1226) eligió vivir el Evangelio <em>sine glossa</em>: sin glosas, sin medias tintas. Hijo de un mercader rico, escuchó a Cristo crucificado pedirle: «Repara mi Iglesia». Desde entonces, su vida se hizo canto, fraternidad y servicio.
            </p>
            <p>
              Fundó la <strong>Orden de los Hermanos Menores</strong>, abrazó la <strong>Dama Pobreza</strong>, predicó a las aves, dialogó con el sultán en plena cruzada, recibió los <strong>estigmas</strong> en La Verna y compuso el <em>Cántico de las Criaturas</em>, primer poema de la literatura italiana.
            </p>
            <p>
              En el año 2026 la Iglesia universal celebra los <strong>800 años de su Pascua</strong>. Para nuestra familia virreyista es una ocasión jubilar: redescubrir la frescura del Evangelio en clave franciscana.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="text-center mb-14">
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-3">Línea del tiempo</p>
          <h2 className="font-display text-4xl md:text-5xl text-primary">Una vida entregada a Cristo</h2>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px timeline-line md:-translate-x-px" />
          {TIMELINE.map((event, i) => {
            const isLeft = i % 2 === 0;
            const Icon = event.icon;
            return (
              <div key={i} className={`relative mb-10 grid md:grid-cols-2 gap-6 items-center`} data-testid={`timeline-event-${i}`}>
                <div className="absolute left-8 md:left-1/2 -translate-x-1/2 z-10">
                  <div className="w-16 h-16 rounded-full bg-card border-4 border-background flex items-center justify-center text-primary shadow-md">
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
                <div className={`pl-24 md:pl-0 ${isLeft ? "md:pr-16 md:text-right" : "md:col-start-2 md:pl-16"}`}>
                  <p className="font-display text-3xl text-secondary">{event.year}</p>
                  <h3 className="font-display text-2xl text-primary mt-1">{event.title}</h3>
                  <p className="mt-2 text-foreground/70 leading-relaxed">{event.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Quotes */}
      <section className="bg-primary text-primary-foreground py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-3 text-center">Frases célebres</p>
          <h2 className="font-display text-4xl md:text-5xl text-center mb-12">La voz del Poverello</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {QUOTES.map((q, i) => (
              <blockquote key={i} className="rounded-2xl p-7 bg-primary-foreground/5 border border-primary-foreground/10 hover:bg-primary-foreground/10 transition-all" data-testid={`quote-${i}`}>
                <p className="font-display text-xl leading-snug italic">“{q}”</p>
                <p className="mt-4 text-xs tracking-[0.2em] uppercase text-secondary">San Francisco de Asís</p>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Contenidos editables — Oración del Papa León y reflexiones */}
      {extra.length > 0 && (
        <section className="container mx-auto px-4 sm:px-6 lg:px-12 py-20" data-testid="centenario-content">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-secondary mb-3">Pastoral jubilar</p>
            <h2 className="font-display text-4xl md:text-5xl text-primary">Voces del jubileo</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {extra.map((b) => (
              <article key={b.id} className="rounded-2xl bg-card border border-border p-7 hover:shadow-md transition-all" data-testid={`cb-${b.id}`}>
                {b.category && (
                  <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-secondary mb-3">{b.category}</p>
                )}
                <h3 className="font-display text-2xl text-primary leading-tight mb-4">{b.title}</h3>
                {b.image_url && (
                  <img src={b.image_url} alt={b.title} className="w-full rounded-xl mb-4 object-cover max-h-72" />
                )}
                <pre className="font-display text-base leading-relaxed whitespace-pre-wrap font-normal text-foreground/85" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {b.body}
                </pre>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
